import React, { useState, useEffect, useCallback } from "react";
import aiAutomationApi from "../../utils/aiAutomationApi";
import { getCid } from "../../utils/encryptedStorage";

const iconUrl = process.env.PUBLIC_URL ? process.env.PUBLIC_URL + "/static/library-icon.png" : "/static/library-icon.png";

const tabs = [
  { key: "all", label: "All" },
  { key: "call", label: "Call" },
  { key: "email", label: "Email" },
  { key: "linkedin", label: "Linkedin Messages" },
];

/**
 * Normalize the `type` field from the API into one of our tab keys.
 * DB values may be: "Call", "call", "Email", "email", "LinkedIn", "linkedin", etc.
 */
const normalizeType = (raw) => {
  if (!raw) return "call"; // default fallback
  const lower = raw.trim().toLowerCase();
  if (lower.includes("email")) return "email";
  if (lower.includes("linkedin")) return "linkedin";
  return "call"; // anything else (call, phone, etc.)
};

const SavedLibraryPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [modal, setModal] = useState({ open: false, template: null });

  // API-driven template state
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch outreach templates from backend
  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const cid = getCid();
      if (!cid) {
        setError("Company ID not found. Please log in again.");
        setIsLoading(false);
        return;
      }
      const data = await aiAutomationApi.get(`/outreach/templates?cid=${cid}`);
      // data is expected to be an array of template objects
      const list = Array.isArray(data) ? data : data?.templates ?? data?.data ?? [];
      // Map DB fields to display-friendly shape
      const mapped = list.map((t) => ({
        id: t.mid,
        type: normalizeType(t.type),
        name: t.template_name,
        content: t.content,
        createdAt: t.create_at,
        createdBy: t.create_by,
      }));
      setTemplates(mapped);
    } catch (err) {
      console.error("Failed to fetch outreach templates:", err);
      setError(err.message || "Failed to load templates.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter helpers
  const callTemplates = templates.filter((t) => t.type === "call");
  const emailTemplates = templates.filter((t) => t.type === "email");
  const linkedinTemplates = templates.filter((t) => t.type === "linkedin");

  const renderTemplates = (list, label) => {
    if (!list.length) return null;
    return (
      <div className="mb-8">
        <h3 className="font-semibold text-slate-700 dark:text-white mb-3 text-base">{label} Templates</h3>
        <div className="flex flex-wrap gap-6">
          {list.map((tpl) => (
            <button
              key={tpl.id}
              className="flex flex-col items-center w-32 focus:outline-none"
              onClick={() => setModal({ open: true, template: tpl })}
              type="button"
            >
              <img src={iconUrl} alt="template icon" className="w-16 h-16 mb-2" />
              <div className="text-xs text-slate-700 dark:text-gray-200 text-center leading-tight">{tpl.name}</div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full flex bg-[#F6F7F9] dark:bg-gray-900">
      <main className="flex-1 min-h-screen bg-white dark:bg-gray-900 px-10 p-[2.5rem] pb-4 transition-colors duration-250">
        {/* Tabs */}
        <div className="flex gap-8 border-b border-gray-200 dark:border-gray-700 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pb-3 px-1 text-base font-medium transition-colors duration-150 border-b-2 ${
                activeTab === tab.key
                  ? "border-primary-400 text-primary-700 dark:text-primary-300"
                  : "border-transparent text-gray-500 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Templates */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <svg className="animate-spin h-8 w-8 mb-3" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <span className="text-sm">Loading templates…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-red-500 dark:text-red-400 text-sm mb-3">{error}</p>
            <button
              onClick={fetchTemplates}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm hover:bg-primary-700 transition-colors"
              type="button"
            >
              Retry
            </button>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-3">
              <path d="M19 11H5M19 11C20.1046 11 21 11.8954 21 13V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V13C3 11.8954 3.89543 11 5 11M19 11V9C19 7.89543 18.1046 7 17 7M5 11V9C5 7.89543 5.89543 7 7 7M7 7V5C7 3.89543 7.89543 3 9 3H15C16.1046 3 17 3.89543 17 5V7M7 7H17" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-sm">No saved templates yet.</span>
          </div>
        ) : (
          <>
            {(activeTab === "all" || activeTab === "call") && renderTemplates(callTemplates, "Call")}
            {(activeTab === "all" || activeTab === "email") && renderTemplates(emailTemplates, "Email")}
            {(activeTab === "all" || activeTab === "linkedin") && renderTemplates(linkedinTemplates, "LinkedIn Message")}
          </>
        )}
      </main>

      {/* Modal Popup */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[877px] mx-4 relative transition-colors duration-250">
            {/* Close button */}
            <button
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-white transition-colors"
              onClick={() => setModal({ open: false, template: null })}
              aria-label="Close"
              type="button"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-700 px-8 py-5">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{modal.template?.name}</h2>
            </div>

            {/* Content */}
            <div className="px-8 py-6">
              {/* Call Script Box */}
              <div className="rounded-lg bg-gradient-to-br from-blue-50 to-teal-50 dark:from-gray-900 dark:to-gray-800 p-6 mb-6 relative border border-blue-100 dark:border-gray-700 transition-colors duration-250">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                    {modal.template?.type === "email" ? "Email Script" : modal.template?.type === "linkedin" ? "LinkedIn Script" : "Call Script"}
                  </div>
                  {/* Bookmark Icon */}
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="url(#bookmark-gradient)" className="flex-shrink-0">
                    <defs>
                      <linearGradient id="bookmark-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="15.87%" stopColor="#3B5BDB" />
                        <stop offset="83.55%" stopColor="#20C997" />
                      </linearGradient>
                    </defs>
                    <path d="M5 2C5 1.44772 5.44772 1 6 1H14C14.5523 1 15 1.44772 15 2V18.5L10 15L5 18.5V2Z" />
                  </svg>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-100 whitespace-pre-wrap leading-relaxed">
                  {modal.template?.content}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 justify-center pb-2">
                <button
                  className="px-10 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 font-medium text-base bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-w-[160px]"
                  onClick={() => {
                    const blob = new Blob([modal.template?.content], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${modal.template?.name || 'template'}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  type="button"
                >
                  Download
                </button>
                <button
                  className="px-10 py-3 rounded-lg bg-[#5B5FED] dark:bg-primary-700 text-white font-medium text-base hover:bg-[#4A4EDD] dark:hover:bg-primary-800 transition-colors min-w-[160px]"
                  onClick={() => {
                    navigator.clipboard.writeText(modal.template?.content || '');
                  }}
                  type="button"
                >
                  Copy Content
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedLibraryPage;
